import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { brainHealthAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { AlertTriangle, X } from 'lucide-react';
import { GoLiveDialog } from '../brain/GoLiveDialog';
import { useAIState } from '../../contexts/AIStateContext';

// Compact, dismissible-per-session banner that makes the #1 activation blocker
// (SIMULATED mode / disconnected mailbox / subscription mismatch) impossible to
// miss. Additive: renders nothing when the Brain is LIVE and unblocked.
export const ActivationBanner = () => {
  const navigate = useNavigate();
  const { planAiEnabled, aiStatusLoaded } = useAIState();
  const [blocker, setBlocker] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);

  useEffect(() => {
    let active = true;
    brainHealthAPI.getCached()
      .then((d) => {
        if (!active || !d) return;
        const high = (d.blockers || []).find((b) => b.severity === 'high');
        if (high) setBlocker(high);
      })
      .catch(() => { /* silent — banner is best-effort */ });
    return () => { active = false; };
  }, []);

  if (!blocker || dismissed) return null;
  // Manual Pro (no-AI plan): SIMULATED/LIVE Brain blockers don't apply — hide it.
  if (aiStatusLoaded && planAiEnabled === false) return null;
  try { if (sessionStorage.getItem(`lk_actbanner_${blocker.code}`)) return null; } catch (_) {}

  const onCta = async () => {
    if (blocker.code === 'simulated_mode') { setGoLiveOpen(true); return; }
    // Phase 4: automation heartbeat down → POST to run-engine-now instead of navigating.
    if (blocker.cta_action === 'run_engine_now') {
      try {
        const r = await brainHealthAPI.runEngineNow();
        const stats = r?.data?.data?.stats || r?.data?.stats || {};
        // Optimistically clear the banner — user can re-open by refreshing.
        try { sessionStorage.setItem(`lk_actbanner_${blocker.code}`, '1'); } catch (_) {}
        setDismissed(true);
        brainHealthAPI.invalidate?.();
        // Best-effort user feedback via a toast if available.
        try {
          const { toast } = await import('sonner');
          toast.success(
            `Engine forced — ${stats.cadences_executed || 0} cadence(s), ${stats.drafts_unstuck || 0} draft(s) unstuck`
          );
        } catch (_) {}
      } catch (_e) {
        try {
          const { toast } = await import('sonner');
          toast.error('Could not force engine — please retry in a minute.');
        } catch (_) {}
      }
      return;
    }
    if (blocker.cta_path) navigate(blocker.cta_path);
  };

  return (
    <div
      className="mb-4 flex items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2.5"
      data-testid="activation-banner"
    >
      <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
      <p className="text-sm text-foreground/90 flex-1">{blocker.label}</p>
      <GoLiveDialog open={goLiveOpen} onOpenChange={setGoLiveOpen} />
      {(blocker.cta_path || blocker.code === 'simulated_mode') && (
        <Button
          size="sm"
          className="h-7 text-xs shrink-0"
          onClick={onCta}
          data-testid="activation-banner-cta"
        >
          {blocker.cta_label}
        </Button>
      )}
      <button
        aria-label="Dismiss"
        className="text-muted-foreground hover:text-foreground shrink-0"
        onClick={() => {
          try { sessionStorage.setItem(`lk_actbanner_${blocker.code}`, '1'); } catch (_) {}
          setDismissed(true);
        }}
        data-testid="activation-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default ActivationBanner;
